import React, { Key, useEffect, useMemo, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import './BoostCredit.scss'
import filterIcon from '../../../../../../BoostXpressAdmin/Assets/Icon/Users/filter.svg'
import arrowLeft from '../../../../../../BoostXpressAdmin/Assets/Icon/Users/arrow-left.png'
import arrowRight from '../../../../../../BoostXpressAdmin/Assets/Icon/Users/arrow-right.svg'
import { boostCreditBXService } from '../../../../../../BoostXpressAdmin/Services/boostCreditBXService'
import { BoostCreditFacility, BoostCreditConfig, BoostCreditDrawdown } from 'BoostXpressAdmin/Model/boostCreditXModel'
import { HorizontalLoader, ModalWrapper } from 'Components'
import { handleError, handleSuccess } from 'Helpers'
import { SubmitHandler, useForm } from 'react-hook-form'
import { DateTimeFormatter } from 'Helpers/DateTimeFormatter'
import { formatString } from 'Helpers/formatString'
import { ToastContainer } from 'react-toastify' 

const formatCurrency = (amount: number) => `₦${amount.toLocaleString()}`

const BoostCredit = () => {
  const navigate = useNavigate()
  const [pageNumber, setPageNumber] = useState(1)
  const [sortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [facilities, setFacilities] = useState<BoostCreditFacility[]>([])
  const [total, setTotal] = useState(0)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  // Filter states
  const [showFilters, setShowFilters] = useState(false)
  const [status, setStatus] = useState('')

  // Tab state
  const [activeTab, setActiveTab] = useState<'customers' | 'drawdowns'>('customers')

  // Drawdown states
  const [drawdowns, setDrawdowns] = useState<BoostCreditDrawdown[]>([])
  const [drawdownsLoading, setDrawdownsLoading] = useState(false)
  const [drawdownsTotal, setDrawdownsTotal] = useState(0)
  const [drawdownsPageNumber, setDrawdownsPageNumber] = useState(1)
  const [drawdownsSearchTerm, setDrawdownsSearchTerm] = useState('')
  const [drawdownsStatus, setDrawdownsStatus] = useState('')
  const [drawdownsCoverage, setDrawdownsCoverage] = useState('')
  const [drawdownsSortOrder, setDrawdownsSortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [drawdownsHasOutstanding, setDrawdownsHasOutstanding] = useState<string>('')
  const [showDrawdownFilters, setShowDrawdownFilters] = useState(false)

  const [pageSize, setPageSize] = useState(50)

  // Config states
  const [config, setConfig] = useState<BoostCreditConfig | null>(null)
  const [configLoading, setConfigLoading] = useState(false)

  // Modal states
  const [isCreateFacilityModalOpen, setIsCreateFacilityModalOpen] = useState(false)
  const [isCreateFacilitySubmitting, setIsCreateFacilitySubmitting] = useState(false)
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false)
  const [isConfigSubmitting, setIsConfigSubmitting] = useState(false)
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false)
  const [isLimitSubmitting, setIsLimitSubmitting] = useState(false)
  const [selectedFacility, setSelectedFacility] = useState<BoostCreditFacility | null>(null)

  const {
    register: registerCreateFacility,
    handleSubmit: handleSubmitCreateFacility,
    reset: resetCreateFacility,
  } = useForm({
    defaultValues: {
      userId: '',
      grossLimitMajor: 0,
    },
  })

  const {
    register: registerConfig,
    handleSubmit: handleSubmitConfig,
    reset: resetConfig,
  } = useForm({
    defaultValues: {
      enabled: true,
      unlockCumulativeThresholdMajor: 0,
      eligibilityWindowDays: 0,
      eligibilityMinOrders: 0,
      eligibilityMultiplier: 0,
      perUserMaxLimitMajor: 0,
      globalCreditLimitMajor: 0,
      dueWindowDays: 0,
      directDebitMandateAmountMajor: 0,
      directDebitMandateDurationDays: 0,
      eligibilityMinAccountAgeDays: 0,
    },
  })

  const {
    register: registerLimit,
    handleSubmit: handleSubmitLimit,
    reset: resetLimit,
  } = useForm({
    defaultValues: {
      grossLimitMajor: 0,
    },
  })

  useEffect(() => {
    const trimmed = searchTerm.trim()
    if (!trimmed) {
      setPageNumber(1)
      return
    }
    setPageNumber(1)
  }, [searchTerm])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        setIsLoading(true)

        const params: any = {
          page: pageNumber,
          limit: 50,
          sortOrder: sortOrder,
        }

        if (status) params.status = status
        if (searchTerm.trim()) params.userResourceId = searchTerm.trim()

        const res = await boostCreditBXService.index(params)
        if (cancelled) return
        setFacilities(res.data?.data ?? [])
        setTotal(res.data?.meta?.pagination?.total ?? res.data?.meta?.total ?? 0)
        setPageSize(res.data?.meta?.pagination?.per_page ?? 50)
      } catch (e) {
        if (!cancelled) {
          setFacilities([])
          setTotal(0)
          setPageSize(50)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [pageNumber, sortOrder, status, searchTerm])

  useEffect(() => {
    let cancelled = false

    const loadConfig = async () => {
      try {
        setConfigLoading(true)
        const response = await boostCreditBXService.getConfig()
        if (cancelled) return
        setConfig(response.data.data ?? null)
        resetConfig({
          enabled: response.data.data?.enabled ?? true,
          unlockCumulativeThresholdMajor: response.data.data?.unlockCumulativeThresholdMajor ?? 0,
          eligibilityWindowDays: response.data.data?.eligibilityWindowDays ?? 0,
          eligibilityMinOrders: response.data.data?.eligibilityMinOrders ?? 0,
          eligibilityMultiplier: response.data.data?.eligibilityMultiplier ?? 0,
          perUserMaxLimitMajor: response.data.data?.perUserMaxLimitMajor ?? 0,
          globalCreditLimitMajor: response.data.data?.globalCreditLimitMajor ?? 0,
          dueWindowDays: response.data.data?.dueWindowDays ?? 0,
          directDebitMandateAmountMajor: response.data.data?.directDebitMandateAmountMajor ?? 0,
          directDebitMandateDurationDays: response.data.data?.directDebitMandateDurationDays ?? 0,
          eligibilityMinAccountAgeDays: response.data.data?.eligibilityMinAccountAgeDays ?? 0,
        })
      } catch (error) {
        if (cancelled) {
          setConfig(null)
          handleError(error as any)
        }
      } finally {
        if (!cancelled) setConfigLoading(false)
      }
    }

    loadConfig()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (target?.closest('[data-menu-root="true"]')) return
      setOpenMenuId(null)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [])

  // Load drawdowns
  useEffect(() => {
    if (activeTab !== 'drawdowns') return

    let cancelled = false
    const loadDrawdowns = async () => {
      try {
        setDrawdownsLoading(true)

        const params: any = {
          page: drawdownsPageNumber,
          limit: 50,
          sortOrder: drawdownsSortOrder,
        }

        if (drawdownsStatus) params.status = drawdownsStatus
        if (drawdownsCoverage) params.coverage = drawdownsCoverage
        if (drawdownsHasOutstanding) params.hasOutstandingBalance = drawdownsHasOutstanding === 'true'
        if (drawdownsSearchTerm.trim()) params.userResourceId = drawdownsSearchTerm.trim()

        const res = await boostCreditBXService.getDrawdowns(params)
        if (cancelled) return
        setDrawdowns(res.data?.data ?? [])
        setDrawdownsTotal(res.data?.data?.length ?? 0)
      } catch (e) {
        if (!cancelled) {
          setDrawdowns([])
          setDrawdownsTotal(0)
        }
      } finally {
        if (!cancelled) setDrawdownsLoading(false)
      }
    }
    loadDrawdowns()
    return () => {
      cancelled = true
    }
  }, [activeTab, drawdownsPageNumber, drawdownsSortOrder, drawdownsStatus, drawdownsCoverage, drawdownsHasOutstanding, drawdownsSearchTerm])

  const drawdownsTotalPages = useMemo(() => {
    if (!drawdownsTotal) return 1
    return Math.max(1, Math.ceil(drawdownsTotal / 50))
  }, [drawdownsTotal])

  const totalPages = useMemo(() => {
    if (!total) return 1
    return Math.max(1, Math.ceil(total / (pageSize || 50)))
  }, [pageSize, total])

  const handleCreateFacilitySubmit: SubmitHandler<{
    userId: string
    grossLimitMajor: number
  }> = async (data) => {
    setIsCreateFacilitySubmitting(true)
    try {
      await boostCreditBXService.createFacility({
        userId: data.userId,
        grossLimitMajor: Number(data.grossLimitMajor),
      })
      handleSuccess('Facility created successfully')
      setIsCreateFacilityModalOpen(false)
      resetCreateFacility()

      // Reload facilities
      const params: any = {
        page: pageNumber,
        limit: 50,
        sortOrder: sortOrder,
      }
      if (status) params.status = status
      if (searchTerm.trim()) params.userResourceId = searchTerm.trim()

      const res = await boostCreditBXService.index(params)
      setFacilities(res.data?.data ?? [])
      setTotal(res.data?.meta?.pagination?.total ?? res.data?.meta?.total ?? 0)
    } catch (error) {
      handleError(error as any)
    } finally {
      setIsCreateFacilitySubmitting(false)
    }
  }

  const handleConfigSubmit: SubmitHandler<any> = async (data) => {
    setIsConfigSubmitting(true)
    try {
      await boostCreditBXService.updateConfig({
        enabled: data.enabled === 'true' ? true : false,
        unlockCumulativeThresholdMajor: Number(data.unlockCumulativeThresholdMajor),
        eligibilityWindowDays: Number(data.eligibilityWindowDays),
        eligibilityMinOrders: Number(data.eligibilityMinOrders),
        eligibilityMultiplier: Number(data.eligibilityMultiplier),
        perUserMaxLimitMajor: Number(data.perUserMaxLimitMajor),
        globalCreditLimitMajor: Number(data.globalCreditLimitMajor),
        dueWindowDays: Number(data.dueWindowDays),
        directDebitMandateAmountMajor: Number(data.directDebitMandateAmountMajor),
        directDebitMandateDurationDays: Number(data.directDebitMandateDurationDays),
        eligibilityMinAccountAgeDays: Number(data.eligibilityMinAccountAgeDays),
      })
      handleSuccess('Configuration updated successfully')
      setIsConfigModalOpen(false)

      // Reload config
      const response = await boostCreditBXService.getConfig()
      setConfig(response.data.data ?? null)
      resetConfig({
        enabled: response.data.data?.enabled ?? true,
        unlockCumulativeThresholdMajor: response.data.data?.unlockCumulativeThresholdMajor ?? 0,
        eligibilityWindowDays: response.data.data?.eligibilityWindowDays ?? 0,
        eligibilityMinOrders: response.data.data?.eligibilityMinOrders ?? 0,
        eligibilityMultiplier: response.data.data?.eligibilityMultiplier ?? 0,
        perUserMaxLimitMajor: response.data.data?.perUserMaxLimitMajor ?? 0,
        globalCreditLimitMajor: response.data.data?.globalCreditLimitMajor ?? 0,
        dueWindowDays: response.data.data?.dueWindowDays ?? 0,
        directDebitMandateAmountMajor: response.data.data?.directDebitMandateAmountMajor ?? 0,
        directDebitMandateDurationDays: response.data.data?.directDebitMandateDurationDays ?? 0,
        eligibilityMinAccountAgeDays: response.data.data?.eligibilityMinAccountAgeDays ?? 0,
      })
    } catch (error) {
      handleError(error as any)
    } finally {
      setIsConfigSubmitting(false)
    }
  }

  const handleSuspend = async (facilityId: string) => {
    const confirmed = window.confirm('Are you sure you want to suspend this facility?')
    if (!confirmed) return

    try {
      await boostCreditBXService.suspendFacility(facilityId)
      handleSuccess('Facility suspended successfully')
      setOpenMenuId(null)

      // Reload facilities
      const params: any = {
        page: pageNumber,
        limit: 50,
        sortOrder: sortOrder,
      }
      if (status) params.status = status
      if (searchTerm.trim()) params.userResourceId = searchTerm.trim()

      const res = await boostCreditBXService.index(params)
      setFacilities(res.data?.data ?? [])
      setTotal(res.data?.meta?.pagination?.total ?? res.data?.meta?.total ?? 0)
    } catch (error) {
      handleError(error as any)
    }
  }

  const handleReinstate = async (facilityId: string) => {
    const confirmed = window.confirm('Are you sure you want to reinstate this facility?')
    if (!confirmed) return

    try {
      await boostCreditBXService.reinstateFacility(facilityId)
      handleSuccess('Facility reinstated successfully')
      setOpenMenuId(null)

      // Reload facilities
      const params: any = {
        page: pageNumber,
        limit: 50,
        sortOrder: sortOrder,
      }
      if (status) params.status = status
      if (searchTerm.trim()) params.userResourceId = searchTerm.trim()

      const res = await boostCreditBXService.index(params)
      setFacilities(res.data?.data ?? [])
      setTotal(res.data?.meta?.pagination?.total ?? res.data?.meta?.total ?? 0)
    } catch (error) {
      handleError(error as any)
    }
  }

  const openLimitModal = (facility: BoostCreditFacility) => {
    setSelectedFacility(facility)
    resetLimit({ grossLimitMajor: facility.grossLimitMajor })
    setIsLimitModalOpen(true)
    setOpenMenuId(null)
  }

  const handleLimitSubmit: SubmitHandler<{ grossLimitMajor: number }> = async (data) => {
    if (!selectedFacility) return
    setIsLimitSubmitting(true)
    try {
      await boostCreditBXService.limitFacility(selectedFacility.resourceId, {
        grossLimitMajor: Number(data.grossLimitMajor),
      })
      handleSuccess('Facility limit updated successfully')
      setIsLimitModalOpen(false)
      setSelectedFacility(null)

      // Reload facilities
      const params: any = {
        page: pageNumber,
        limit: 50,
        sortOrder: sortOrder,
      }
      if (status) params.status = status
      if (searchTerm.trim()) params.userResourceId = searchTerm.trim()

      const res = await boostCreditBXService.index(params)
      setFacilities(res.data?.data ?? [])
      setTotal(res.data?.meta?.pagination?.total ?? res.data?.meta?.total ?? 0)
    } catch (error) {
      handleError(error as any)
    } finally {
      setIsLimitSubmitting(false)
    }
  }

  const clearFilters = () => {
    setStatus('')
    setSearchTerm('')
    setPageNumber(1)
  }

  const configCards = useMemo(() => {
    if (!config) return []
    return [
      {
        label: 'Status',
        value: config.enabled ? 'Enabled' : 'Disabled',
        tone: config.enabled ? 'green' : 'red',
      },
      {
        label: 'Global Limit',
        value: formatCurrency(config.globalCreditLimitMajor),
        tone: 'blue',
      },
      {
        label: 'Per User Max Limit',
        value: formatCurrency(config.perUserMaxLimitMajor),
        tone: 'purple',
      },
      {
        label: 'Unlock Threshold',
        value: formatCurrency(config.unlockCumulativeThresholdMajor),
        tone: 'orange',
      },
      {
        label: 'Eligibility Window',
        value: `${config.eligibilityWindowDays} days`,
        tone: 'green',
      },
      {
        label: 'Min Orders Required',
        value: config.eligibilityMinOrders,
        tone: 'blue',
      },
      {
        label: 'Eligibility Multiplier',
        value: config.eligibilityMultiplier,
        tone: 'purple',
      },
      {
        label: 'Due Window',
        value: `${config.dueWindowDays} days`,
        tone: 'orange',
      },
      {
        label: 'Mandate Amount',
        value: formatCurrency(config.directDebitMandateAmountMajor),
        tone: 'green',
      },
      {
        label: 'Mandate Duration',
        value: `${config.directDebitMandateDurationDays} days`,
        tone: 'blue',
      },
      {
        label: 'Min Account Age',
        value: `${config.eligibilityMinAccountAgeDays} days`,
        tone: 'purple',
      },
    ]
  }, [config])

  return (
    <div className='bx-boost-credit'>
      <div className='row w-100'>
        <div className='col-md-8'>
          <h2 className='bx-boost-credit__title'>Boost Flex Management</h2>
        </div>

        <div className='col-6 col-md-2'>
          <button
            className='btn btn-default bx-action-btn- w-100 mt-2 mt-md-auto'
            onClick={() => setIsCreateFacilityModalOpen(true)}
          >
            <span className='bx-action-label'>Create Facility</span>
          </button>
        </div>

        <div className='col-6 col-md-2'>
          <button
            className='btn btn-default bx-action-btn- w-100 mt-2 mt-md-auto'
            onClick={() => setIsConfigModalOpen(true)}
          >
            <span className='bx-action-label'>Config</span>
          </button>
        </div>
      </div>

      {/* Config Cards */}
      <div className='bx-boost-credit__cards'>
        {configLoading ? (
          <div className='stat-card'>
            <div className='stat-content'>
              <div className='stat-title'>Loading config...</div>
            </div>
          </div>
        ) : configCards.length > 0 ? (
          <div className='row'>
            {configCards.map((card, index) => (
              <div key={index} className='col-md-3 mt-3'>
                <div className='stat-card' style={{ borderLeftColor: getCardColor(card.tone) }}>
                  <div className='stat-content'>
                    <div className='stat-title'>{card.label}</div>
                    <div className='stat-value'>{card.value}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className='stat-card' style={{ borderLeftColor: '#e74c3c' }}>
            <div className='stat-content'>
              <div className='stat-title'>Failed to load config</div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className='bx-boost-credit__tabs mb-0'>
        <button
          className={`bx-boost-credit__tab ${activeTab === 'customers' ? 'bx-boost-credit__tab--active' : ''}`}
          onClick={() => setActiveTab('customers')}
        >
          <svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path d='M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
            <circle cx='9' cy='7' r='4' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
            <path d='M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
            <path d='M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
          </svg>
          Customers
        </button>
        <button
          className={`bx-boost-credit__tab ${activeTab === 'drawdowns' ? 'bx-boost-credit__tab--active' : ''}`}
          onClick={() => setActiveTab('drawdowns')}
        >
          <svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <rect x='2' y='5' width='20' height='14' rx='2' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
            <path d='M16 10H8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
            <path d='M16 14H8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
            <circle cx='12' cy='10' r='1' fill='currentColor'/>
            <circle cx='12' cy='14' r='1' fill='currentColor'/>
          </svg>
          DrawDowns
        </button>
      </div>

      <div className='bx-boost-credit__container'>
        {activeTab === 'customers' && (
          <>
            <div className='bx-boost-credit__toolbar row g-2 align-items-center'>
          <div className='col-12 col-md-8 col-xxl-11'>
            <div className='bx-search-wrapper w-100'>
              <div className='bx-search-icon'>
                <svg
                  width='17'
                  height='17'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                    stroke='#868484'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <input
                type='text'
                placeholder='Search by user resource ID'
                className='bx-search-input'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className='col-6 col-md-2 col-xxl-1 position-relative'>
            <button className='bx-action-btn w-100' onClick={() => setShowFilters(!showFilters)}>
              <img src={filterIcon} alt='' className='bx-action-icon' />
              <span className='bx-action-label'>Filters</span>
            </button>

            {showFilters && (
              <div className='bx-users-filters-dropdown'>
                <div className='bx-filters-header'>
                  <h5>Filter Facilities</h5>
                  <button className='bx-filters-close' onClick={() => setShowFilters(false)}>
                    ×
                  </button>
                </div>

                <div className='bx-filters-body'>
                  <div className='bx-filter-group'>
                    <label>Status</label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className='form-control'
                    >
                      <option value=''>All Statuses</option>
                      <option value='pending_activation'>Pending Activation</option>
                      <option value='active'>Active</option>
                      <option value='paused'>Paused</option>
                      <option value='suspended'>Suspended</option>
                      <option value='blacklisted'>Blacklisted</option>
                    </select>
                  </div>
                </div>

                <div className='bx-filters-footer'>
                  <button className='btn btn-secondary' onClick={clearFilters}>
                    Clear Filters
                  </button>
                  <button className='btn btn-primary' onClick={() => setShowFilters(false)}>
                    Apply Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className='bx-table-wrapper'>
          <table className='bx-table'>
            <thead>
              <tr>
                <th className='bx-th-name'>S/N</th>
                <th className='bx-th-name'>User</th>
                {/* <th className='bx-th-email'>Email</th> */}
                <th className='bx-th-phone'>Phone</th>
                <th className='bx-th-phone'>Gross Limit</th>
                <th className='bx-th-phone'>Outstanding</th>
                <th className='bx-th-phone'>Available</th>
                <th className='bx-th-status'>Status</th>
                <th className='bx-th-phone'>Created At</th>
                <th className='bx-th-action'>Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={10} className='bx-td-name'>
                    <HorizontalLoader></HorizontalLoader>
                  </td>
                </tr>
              ) : facilities.length === 0 ? (
                <tr>
                  <td colSpan={10} className='bx-td-name'>
                    No facilities found
                  </td>
                </tr>
              ) : (
                facilities.map((facility: BoostCreditFacility, index: Key) => {
                  const fullName =
                    `${facility.user.firstName ?? ''} ${facility.user.lastName ?? ''}`.trim()

                  return (
                    <tr key={facility.resourceId}>
                      <td className='bx-td-name'>
                        {Number(index) + 1 + (pageNumber - 1) * pageSize}
                      </td>
                      <td className='bx-td-name'>
                        <Link
                          to={`/boostxpress/users/${facility.user.resourceId}`}
                          className='text-dark'
                        >
                          {formatString(fullName)}
                        </Link>
                      </td>

                      <td className='bx-td-phone'>{facility.user.phone || '-'}</td>
                      <td className='bx-td-phone'>{formatCurrency(facility.grossLimitMajor)}</td>
                      <td className='bx-td-phone'>{formatCurrency(facility.outstandingMajor)}</td>
                      <td className='bx-td-phone'>{formatCurrency(facility.availableMajor)}</td>
                      <td className='bx-td-status'>
                        <div className='bx-badge-wrapper'>
                          <span className={`bx-badge bx-badge--${facility.status.toLowerCase()}`}>
                            {formatString(facility.status)}
                          </span>
                        </div>
                      </td>
                      <td className='bx-td-phone'>{DateTimeFormatter(facility.createdAt)}</td>
                      <td className='bx-td-action'>
                        <div className='bx-user-table__menu' data-menu-root='true'>
                          <button
                            type='button'
                            className='bx-user-table__menu-btn'
                            aria-haspopup='menu'
                            aria-expanded={openMenuId === facility.resourceId}
                            onClick={() =>
                              setOpenMenuId((v) =>
                                v === facility.resourceId ? null : facility.resourceId,
                              )
                            }
                          >
                            <svg
                              width='20'
                              height='20'
                              viewBox='0 0 20 20'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <circle cx='10' cy='4.5' r='1.3' fill='#667085' />
                              <circle cx='10' cy='10' r='1.3' fill='#667085' />
                              <circle cx='10' cy='15.5' r='1.3' fill='#667085' />
                            </svg>
                          </button>

                          {openMenuId === facility.resourceId ? (
                            <div className='bx-user-table__menu-dropdown' role='menu'>
                              <button
                                type='button'
                                className='bx-user-table__menu-item'
                                role='menuitem'
                                onClick={() => {
                                  setOpenMenuId(null)
                                  navigate(`/boostxpress/boost-credit/facility/${facility.resourceId}`, {
                                    state: { facility },
                                  })
                                }}
                              >
                                View Details
                              </button>
                              <button
                                type='button'
                                className='bx-user-table__menu-item'
                                role='menuitem'
                                onClick={() => handleSuspend(facility.resourceId)}
                              >
                                Suspend
                              </button>
                              <button
                                type='button'
                                className='bx-user-table__menu-item'
                                role='menuitem'
                                onClick={() => handleReinstate(facility.resourceId)}
                              >
                                Reinstate
                              </button>
                              <button
                                type='button'
                                className='bx-user-table__menu-item'
                                role='menuitem'
                                onClick={() => openLimitModal(facility)}
                              >
                                Limit
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className='bx-pagination'>
          <button
            className='bx-pagination-btn'
            disabled={pageNumber <= 1 || isLoading}
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          >
            <div className='bx-pagination-icon-wrap'>
              <img src={arrowLeft} alt='' className='bx-pagination-icon bx-pagination-icon--left' />
            </div>
            <span className='bx-pagination-text'>Previous</span>
          </button>

          <div className='bx-pagination-numbers'>
            <span className='bx-page-number bx-page-number--active'>{pageNumber}</span>
            <span className='bx-page-ellipsis'>/</span>
            <span className='bx-page-number'>{totalPages}</span>
          </div>

          <button
            className='bx-pagination-btn'
            disabled={pageNumber >= totalPages || isLoading}
            onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
          >
            <span className='bx-pagination-text'>Next</span>
            <img src={arrowRight} alt='' className='bx-pagination-icon' />
          </button>
        </div>
      </>
        )}

        {activeTab === 'drawdowns' && (
          <>
            <div className='bx-boost-credit__toolbar row g-2 align-items-center'>
              <div className='col-12 col-md-8 col-xxl-11'>
                <div className='bx-search-wrapper w-100'>
                  <div className='bx-search-icon'>
                    <svg
                      width='17'
                      height='17'
                      viewBox='0 0 24 24'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                        stroke='#868484'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  </div>
                  <input
                    type='text'
                    placeholder='Search by user resource ID'
                    className='bx-search-input'
                    value={drawdownsSearchTerm}
                    onChange={(e) => setDrawdownsSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div className='col-6 col-md-2 col-xxl-1 position-relative'>
                <button className='bx-action-btn w-100' onClick={() => setShowDrawdownFilters(!showDrawdownFilters)}>
                  <img src={filterIcon} alt='' className='bx-action-icon' />
                  <span className='bx-action-label'>Filters</span>
                </button>

                {showDrawdownFilters && (
                  <div className='bx-users-filters-dropdown'>
                    <div className='bx-filters-header'>
                      <h5>Filter Drawdowns</h5>
                      <button className='bx-filters-close' onClick={() => setShowDrawdownFilters(false)}>
                        ×
                      </button>
                    </div>

                    <div className='bx-filters-body'>
                      <div className='bx-filter-group'>
                        <label>Status</label>
                        <select
                          value={drawdownsStatus}
                          onChange={(e) => setDrawdownsStatus(e.target.value)}
                          className='form-control'
                        >
                          <option value=''>All Statuses</option>
                          <option value='owing'>Owing</option>
                          <option value='partially_repaid'>Partially Repaid</option>
                          <option value='repaid'>Repaid</option>
                          <option value='overdue'>Overdue</option>
                          <option value='defaulted'>Defaulted</option>
                        </select>
                      </div>
                      <div className='bx-filter-group'>
                        <label>Coverage</label>
                        <select
                          value={drawdownsCoverage}
                          onChange={(e) => setDrawdownsCoverage(e.target.value)}
                          className='form-control'
                        >
                          <option value=''>All Coverage</option>
                          <option value='full'>Full</option>
                          <option value='partial'>Partial</option>
                        </select>
                      </div>
                      <div className='bx-filter-group'>
                        <label>Has Outstanding Balance</label>
                        <select
                          value={drawdownsHasOutstanding}
                          onChange={(e) => setDrawdownsHasOutstanding(e.target.value)}
                          className='form-control'
                        >
                          <option value=''>All</option>
                          <option value='true'>Yes</option>
                          <option value='false'>No</option>
                        </select>
                      </div>
                      <div className='bx-filter-group'>
                        <label>Sort Order</label>
                        <select
                          value={drawdownsSortOrder}
                          onChange={(e) => setDrawdownsSortOrder(e.target.value as 'ASC' | 'DESC')}
                          className='form-control'
                        >
                          <option value='DESC'>Descending</option>
                          <option value='ASC'>Ascending</option>
                        </select>
                      </div>
                    </div>

                    <div className='bx-filters-footer'>
                      <button
                        className='btn btn-secondary'
                        onClick={() => {
                          setDrawdownsStatus('')
                          setDrawdownsCoverage('')
                          setDrawdownsHasOutstanding('')
                          setDrawdownsSortOrder('DESC')
                        }}
                      >
                        Clear Filters
                      </button>
                      <button className='btn btn-primary' onClick={() => setShowDrawdownFilters(false)}>
                        Apply Filters
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='bx-table-wrapper'>
              <table className='bx-table'>
                <thead>
                  <tr>
                    <th className='bx-th-name'>S/N</th>
                    <th className='bx-th-name'>User</th>
                    <th className='bx-th-phone'>Order ID</th>
                    <th className='bx-th-phone'>Principal</th>
                    <th className='bx-th-phone'>Outstanding</th>
                    <th className='bx-th-phone'>Paid</th>
                    <th className='bx-th-status'>Status</th>
                    <th className='bx-th-phone'>Coverage</th>
                    <th className='bx-th-phone'>Due Date</th>
                    <th className='bx-th-phone'>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {drawdownsLoading ? (
                    <tr>
                      <td colSpan={10} className='bx-td-name'>
                        <HorizontalLoader></HorizontalLoader>
                      </td>
                    </tr>
                  ) : drawdowns.length === 0 ? (
                    <tr>
                      <td colSpan={10} className='bx-td-name'>
                        No drawdowns found
                      </td>
                    </tr>
                  ) : (
                    drawdowns.map((drawdown: BoostCreditDrawdown, index: Key) => {
                      const fullName =
                        `${drawdown.user.firstName ?? ''} ${drawdown.user.lastName ?? ''}`.trim()

                      return (
                        <tr key={drawdown.resourceId}>
                          <td className='bx-td-name'>
                            {Number(index) + 1 + (drawdownsPageNumber - 1) * 50}
                          </td>
                          <td className='bx-td-name'>
                            <Link
                              to={`/boostxpress/users/${drawdown.user.resourceId}`}
                              className='text-dark'
                            >
                              {formatString(fullName)}
                            </Link>
                          </td>
                          <td className='bx-td-phone'>{drawdown.orderResourceId || '-'}</td>
                          <td className='bx-td-phone'>{formatCurrency(drawdown.principalMajor)}</td>
                          <td className='bx-td-phone'>{formatCurrency(drawdown.outstandingMajor)}</td>
                          <td className='bx-td-phone'>{formatCurrency(drawdown.paidMajor)}</td>
                          <td className='bx-td-status'>
                            <div className='bx-badge-wrapper'>
                              <span className={`bx-badge bx-badge--${drawdown.status.toLowerCase()}`}>
                                {formatString(drawdown.status)}
                              </span>
                            </div>
                          </td>
                          <td className='bx-td-phone'>{formatString(drawdown.coverage)}</td>
                          <td className='bx-td-phone'>{DateTimeFormatter(drawdown.dueDate)}</td>
                          <td className='bx-td-phone'>{DateTimeFormatter(drawdown.createdAt)}</td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className='bx-pagination'>
              <button
                className='bx-pagination-btn'
                disabled={drawdownsPageNumber <= 1 || drawdownsLoading}
                onClick={() => setDrawdownsPageNumber((p) => Math.max(1, p - 1))}
              >
                <div className='bx-pagination-icon-wrap'>
                  <img src={arrowLeft} alt='' className='bx-pagination-icon bx-pagination-icon--left' />
                </div>
                <span className='bx-pagination-text'>Previous</span>
              </button>

              <div className='bx-pagination-numbers'>
                <span className='bx-page-number bx-page-number--active'>{drawdownsPageNumber}</span>
                <span className='bx-page-ellipsis'>/</span>
                <span className='bx-page-number'>{drawdownsTotalPages}</span>
              </div>

              <button
                className='bx-pagination-btn'
                disabled={drawdownsPageNumber >= drawdownsTotalPages || drawdownsLoading}
                onClick={() => setDrawdownsPageNumber((p) => Math.min(drawdownsTotalPages, p + 1))}
              >
                <span className='bx-pagination-text'>Next</span>
                <img src={arrowRight} alt='' className='bx-pagination-icon' />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Create Facility Modal */}
      <ModalWrapper
        showModal={isCreateFacilityModalOpen}
        closeModal={() => {
          setIsCreateFacilityModalOpen(false)
          resetCreateFacility()
        }}
        title='Create Facility'
        hideHeader={false}
      >
        <form onSubmit={handleSubmitCreateFacility(handleCreateFacilitySubmit)}>
          <div className='mb-3 mt-4'>
            <label htmlFor='userId' className='form-label'>
              User ID
            </label>
            <input
              id='userId'
              type='text'
              className='form-control'
              {...registerCreateFacility('userId', { required: true })}
            />
          </div>
          <div className='mb-3'>
            <label htmlFor='grossLimitMajor' className='form-label'>
              Gross Limit
            </label>
            <input
              id='grossLimitMajor'
              type='number'
              className='form-control'
              {...registerCreateFacility('grossLimitMajor', { required: true, min: 0 })}
            />
          </div>
          <div className='d-flex justify-content-end gap-2'>
            <button
              type='button'
              className='btn btn-default btn-secondary border-0 bg-secondary px-4'
              onClick={() => {
                setIsCreateFacilityModalOpen(false)
                resetCreateFacility()
              }}
              disabled={isCreateFacilitySubmitting}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='btn btn-default px-4'
              disabled={isCreateFacilitySubmitting}
            >
              {isCreateFacilitySubmitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </ModalWrapper>

      {/* Config Modal */}
      <ModalWrapper
        showModal={isConfigModalOpen}
        closeModal={() => {
          setIsConfigModalOpen(false)
          if (config) {
            resetConfig({
              enabled: config.enabled,
              unlockCumulativeThresholdMajor: config.unlockCumulativeThresholdMajor,
              eligibilityWindowDays: config.eligibilityWindowDays,
              eligibilityMinOrders: config.eligibilityMinOrders,
              eligibilityMultiplier: config.eligibilityMultiplier,
              perUserMaxLimitMajor: config.perUserMaxLimitMajor,
              globalCreditLimitMajor: config.globalCreditLimitMajor,
              dueWindowDays: config.dueWindowDays,
              directDebitMandateAmountMajor: config.directDebitMandateAmountMajor,
              directDebitMandateDurationDays: config.directDebitMandateDurationDays,
              eligibilityMinAccountAgeDays: config.eligibilityMinAccountAgeDays,
            })
          }
        }}
        title='Boost Credit Configuration'
        hideHeader={false}
        size='lg'
      >
        <form onSubmit={handleSubmitConfig(handleConfigSubmit)}>
          <div className='row mt-3'>
            <div className='mb-3'>
              <label htmlFor='enabled' className='form-label-config'>
                Enabled
              </label>
              <select id='enabled' className='form-control' {...registerConfig('enabled')}>
                <option value={'true'}>Yes</option>
                <option value={'false'}>No</option>
              </select>
            </div>
            <div className='mb-3'>
              <label htmlFor='globalCreditLimitMajor' className='form-label-config'>
                Global Limit
              </label>
              <input
                id='globalCreditLimitMajor'
                type='number'
                className='form-control'
                {...registerConfig('globalCreditLimitMajor', { required: true, min: 0 })}
              />
            </div>
            <div className='mb-3'>
              <label htmlFor='perUserMaxLimitMajor' className='form-label-config'>
                Per User Max Limit
              </label>
              <input
                id='perUserMaxLimitMajor'
                type='number'
                className='form-control'
                {...registerConfig('perUserMaxLimitMajor', { required: true, min: 0 })}
              />
            </div>
            <div className='mb-3'>
              <label htmlFor='unlockCumulativeThresholdMajor' className='form-label-config'>
                Unlock Cumulative Threshold
              </label>
              <input
                id='unlockCumulativeThresholdMajor'
                type='number'
                className='form-control'
                {...registerConfig('unlockCumulativeThresholdMajor', { required: true, min: 0 })}
              />
            </div>
            <div className='mb-3'>
              <label htmlFor='eligibilityWindowDays' className='form-label-config'>
                Eligibility Window (Days)
              </label>
              <input
                id='eligibilityWindowDays'
                type='number'
                className='form-control'
                {...registerConfig('eligibilityWindowDays', { required: true, min: 0 })}
              />
            </div>
            <div className='mb-3'>
              <label htmlFor='eligibilityMinOrders' className='form-label-config'>
                Eligibility Min Orders
              </label>
              <input
                id='eligibilityMinOrders'
                type='number'
                className='form-control'
                {...registerConfig('eligibilityMinOrders', { required: true, min: 0 })}
              />
            </div>
            <div className='mb-3'>
              <label htmlFor='eligibilityMultiplier' className='form-label-config'>
                Eligibility Multiplier
              </label>
              <input
                id='eligibilityMultiplier'
                type='number'
                step='0.01'
                className='form-control'
                {...registerConfig('eligibilityMultiplier', { required: true, min: 0 })}
              />
            </div>
            <div className='mb-3'>
              <label htmlFor='dueWindowDays' className='form-label-config'>
                Due Window (Days)
              </label>
              <input
                id='dueWindowDays'
                type='number'
                className='form-control'
                {...registerConfig('dueWindowDays', { required: true, min: 0 })}
              />
            </div>
            <div className='mb-3'>
              <label htmlFor='directDebitMandateAmountMajor' className='form-label-config'>
                Direct Debit Mandate Amount
              </label>
              <input
                id='directDebitMandateAmountMajor'
                type='number'
                className='form-control'
                {...registerConfig('directDebitMandateAmountMajor', { required: true, min: 0 })}
              />
            </div>
            <div className='mb-3'>
              <label htmlFor='directDebitMandateDurationDays' className='form-label-config'>
                Direct Debit Mandate Duration (Days)
              </label>
              <input
                id='directDebitMandateDurationDays'
                type='number'
                className='form-control'
                {...registerConfig('directDebitMandateDurationDays', { required: true, min: 0 })}
              />
            </div>
            <div className='mb-3'>
              <label htmlFor='eligibilityMinAccountAgeDays' className='form-label-config'>
                Eligibility Min Account Age (Days)
              </label>
              <input
                id='eligibilityMinAccountAgeDays'
                type='number'
                className='form-control'
                {...registerConfig('eligibilityMinAccountAgeDays', { required: true, min: 0 })}
              />
            </div>
          </div>
          <div className='d-flex justify-content-end gap-2'>
            <button
              type='button'
              className='btn btn-default btn-secondary border-0 bg-secondary px-4'
              onClick={() => setIsConfigModalOpen(false)}
              disabled={isConfigSubmitting}
            >
              Cancel
            </button>
            <button type='submit' className='btn btn-default px-4' disabled={isConfigSubmitting}>
              {isConfigSubmitting ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </ModalWrapper>

      {/* Limit Modal */}
      <ModalWrapper
        showModal={isLimitModalOpen}
        closeModal={() => {
          setIsLimitModalOpen(false)
          setSelectedFacility(null)
          resetLimit()
        }}
        title='Update Facility Limit'
        hideHeader={false}
      >
        <form onSubmit={handleSubmitLimit(handleLimitSubmit)}>
          <div className='mb-3'>
            <label htmlFor='grossLimitMajor' className='form-label-config'>
              Gross Limit
            </label>
            <input
              id='grossLimitMajor'
              type='number'
              className='form-control'
              {...registerLimit('grossLimitMajor', { required: true, min: 0 })}
            />
          </div>
          <div className='d-flex justify-content-end gap-2'>
            <button
              type='button'
              className='btn btn-secondary'
              onClick={() => {
                setIsLimitModalOpen(false)
                setSelectedFacility(null)
                resetLimit()
              }}
              disabled={isLimitSubmitting}
            >
              Cancel
            </button>
            <button type='submit' className='btn btn-primary' disabled={isLimitSubmitting}>
              {isLimitSubmitting ? 'Updating...' : 'Update'}
            </button>
          </div>
        </form>
      </ModalWrapper>
      <ToastContainer></ToastContainer>
    </div>
  )
}

const getCardColor = (tone: string): string => {
  const colors: Record<string, string> = {
    blue: '#3378f3',
    green: '#2ecc71',
    purple: '#863de5',
    orange: '#f0973d',
    red: '#e74c3c',
  }
  return colors[tone] || '#3378f3'
}

export default BoostCredit
